import { Check, ChevronRight, OctagonAlert, OctagonPause, Repeat2 } from 'lucide-react';
import Link from 'next/link';
import type { ComponentType } from 'react';
import { useTranslation } from '../../../lib/i18n';
import { useBasePath } from '../../../lib/use-base-path';
import { routeLink } from '../../../lib/route-link';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { useRoutines } from '../../../lib/routines';
import { routineStatus } from '../../../lib/routine-status';
import { useNewSince } from '../../../lib/use-last-visited';
import { countNewSince } from '../../../lib/summary-freshness';
import { Badge, Card, IconCountBadge, IconCountBadges, Typography } from '../ui';
import { AgentEntryRow } from '../agents/AgentEntryRow';
import styles from './routines.module.css';

// The compact routines summary — the count per condition and a link into the full Routines list, NOT
// the list itself. Second of the four tiles (Tasks · Routines · Files · Apps) that head Your Agents and
// a Space's Dashboard tab, and built to the same shape as `TasksSummary` beside it (Figma
// `15378:117541`, 249×72): the glyph 4 from the plain "Routines" heading (Heading 04), a row of 12px
// icon-count badges 8 under it, and the drill-in chevron centred against the whole card.
//
// **The heading is the bare word.** It used to read "{count} Routines", which stated the total the
// badges under it already add up to, and cost the tile the width they need.
//
// The three counts are the three things a person wants to know about a set of routines: how many are
// live, how many they stopped, and how many the ACCOUNT stopped — the last being the only one that is
// actionable, which is why it is never folded into "Paused".
//
// The heading line also carries a "N new" badge — routines created since the reader last opened
// `/routines` on this device (`lib/last-visited.ts` + `lib/summary-freshness.ts`), the same OUTLINED
// badge the Tasks, Files and Apps tiles beside it wear. It costs nothing extra: the list is in hand.

// `icon` is the 12px glyph every badge leads with (Figma 15378:117541 — "Use 12px icons for badges"),
// shared 1:1 with native's status icons; `labelKey` is the sentence it replaces, announced on the
// badge's `aria-label`.
const CATS = [
  { key: 'active' as const, labelKey: 'routines.chip.active', variant: 'done' as const, icon: Check },
  { key: 'paused' as const, labelKey: 'routines.chip.paused', variant: 'paused' as const, icon: OctagonPause },
  { key: 'outOfCredits' as const, labelKey: 'routines.chip.outOfCredits', variant: 'waiting-on-you' as const, icon: OctagonAlert },
] satisfies { key: string; labelKey: string; variant: string; icon: ComponentType<{ size?: number }> }[];

export function RoutinesSummary({
  entryRow = false,
  workspaceId,
  workspaceSlug,
  pageName = 'agents',
  onSelect,
}: {
  /** Mobile Your Agents: render the compact single-row entry tile with 12px icon-count badges
   *  (Figma 15311:197080), instead of the desktop heading-over-chips summary. */
  entryRow?: boolean;
  /** Scopes the read to one Space (a Space's Dashboard tab). Omit for the account-wide count. */
  workspaceId?: string;
  /** Carried into the link so the Routines screen opens pre-filtered to that Space. */
  workspaceSlug?: string;
  pageName?: string;
  /** Under the map home the tile is a TAB: this runs instead of navigating (the href stays for cmd-click). */
  onSelect?: () => void;
} = {}) {
  const { t } = useTranslation();
  const basePath = useBasePath();
  const { logPageClickBi } = useGenericBi();
  const { state } = useRoutines(workspaceId);
  // Captured once at mount — see `useNewSince` for why reading the stamp live would make the badge
  // vanish under the reader the moment they came back from `/routines`.
  const since = useNewSince('routines');

  if (state.kind !== 'ready') return null;

  const counts = { active: 0, paused: 0, outOfCredits: 0 };
  for (const routine of state.routines) {
    const status = routineStatus(routine);
    if (status === 'out-of-credits') counts.outOfCredits += 1;
    else if (status === 'paused') counts.paused += 1;
    // A trigger-driven or unscheduled routine is not "Active" — it is counted in the total only, so a
    // chip never promises a schedule that isn't there.
    else if (status === 'active') counts.active += 1;
  }
  const total = state.routines.length;
  const empty = total === 0;
  const shown = CATS.filter((c) => counts[c.key] > 0);
  const newCount = since === null ? 0 : countNewSince(state.routines, since);

  const href = workspaceSlug ? `/routines?workspace=${encodeURIComponent(workspaceSlug)}` : '/routines';
  const openRoutines = (e?: { preventDefault: () => void; metaKey?: boolean; ctrlKey?: boolean; shiftKey?: boolean; button?: number }) => {
    // A modifier click keeps the link's own behaviour (a new tab); a plain click is the tab.
    if (onSelect && !(e?.metaKey || e?.ctrlKey || e?.shiftKey || (e?.button ?? 0) !== 0)) {
      e?.preventDefault();
      onSelect();
    }
    return logPageClickBi({ page_name: pageName, button_name: 'open_routines' });
  };

  // Mobile entry row (Figma 15311:197080): the title, then one 12px icon-count badge per non-empty
  // status on its own status-chip colour, then the drill-in chevron. Mirrors native's `entryRowCard`.
  if (entryRow) {
    return (
      <AgentEntryRow
        title={t('routines.title')}
        href={href}
        onClick={openRoutines}
        testId="agentsRoutinesSummary"
        trailing={
          <IconCountBadges>
            {shown.map((c) => (
              <IconCountBadge key={c.key} icon={c.icon} count={counts[c.key]} variant={c.variant} label={t(c.labelKey, { count: counts[c.key] })} />
            ))}
          </IconCountBadges>
        }
      />
    );
  }

  return (
    <Card component={Link} interactive size="sm" className={styles.summaryCard} {...routeLink(basePath, href)} onClick={openRoutines}>
      <div className={styles.summaryBody}>
        <span className={styles.summaryTitle}>
          {/* `repeat-2`, not `repeat` — the frame draws `16/repeat-2` (Figma 15482:103796), the two
              bent arrows. Lucide's plain `Repeat` is the four-arrow loop and read as a different mark. */}
          <Repeat2 size={16} aria-hidden />
          <Typography variant="h4" color="primary" component="span" noWrap className={styles.summaryTitleText}>
            {t('routines.title')}
          </Typography>
          {newCount > 0 && <Badge variant="agent-profession" className={styles.summaryNew} label={t('summary.newBadge', { count: newCount })} />}
        </span>
        {empty ? (
          <Typography variant="p2" color="secondary">
            {t('routines.chip.none')}
          </Typography>
        ) : (
          <div className={styles.summaryChips}>
            {shown.map((c) => (
              <IconCountBadge key={c.key} icon={c.icon} count={counts[c.key]} variant={c.variant} label={t(c.labelKey, { count: counts[c.key] })} />
            ))}
          </div>
        )}
      </div>
      <ChevronRight size={16} className={`${styles.summaryChevron} dir-flip`} aria-hidden />
    </Card>
  );
}
